/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import {
	SolutionLotDetail,
	MediaSolutionBatches,
	SolutionPowderMixingPayload,
	ValidationErrors,
} from "../../../types/mediaBatchPreparation.types";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices";
import { useTranslation } from "react-i18next";
import { SolutionConsumption } from "../../../types/mediaBatchCommon.types";

interface Props {
	solutionConsumption: SolutionConsumption | undefined; // Added undefined for safety
	mixingParameters: any;
	setMixingParameters: React.Dispatch<React.SetStateAction<any>>;
	selectedSolutionBatch: MediaSolutionBatches | null;
	setSelectedSolutionBatch: React.Dispatch<React.SetStateAction<MediaSolutionBatches | null>>;
	selectedSolutionBatchLot: SolutionLotDetail | null;
	setSelectedSolutionBatchLot: React.Dispatch<React.SetStateAction<SolutionLotDetail | null>>;
	errors: ValidationErrors;
	setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
	
}

const MediaSolutionSelectionBox = ({
	solutionConsumption,
	mixingParameters,
	setMixingParameters,
	selectedSolutionBatch,
	setSelectedSolutionBatch,
	selectedSolutionBatchLot,
	setSelectedSolutionBatchLot,
	errors,
	setErrors,

}: Props) => {
	const { t } = useTranslation();

	// Establish read-only mode strictly based on the prop
	const isReadOnly = !!solutionConsumption;

	const [solutionBatches, setSolutionBatches] = useState<MediaSolutionBatches[]>([]);
	const [solutionBatchLots, setSolutionBatchLots] = useState<SolutionLotDetail[]>([]);

	const getSolutionBatches = async () => {
		const urlParams = {
			page: 1,
			size: 50, // Fixed 'szie' typo here
			sort: "id",
			order: "desc",
			status: "usable",
		};
		try {
			const response = await MediaBatchPreparationInitServices.getMediaSolutionBatches(urlParams);
			setSolutionBatches(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getSolutionBatchLots = async () => {
		if (!selectedSolutionBatch) return;
		const urlParams = {
			sort: "id",
        	order: "asc",
			solution_batch_id: selectedSolutionBatch.id,

		};
		try {
			const response = await MediaBatchPreparationInitServices.getMediaSolutionBatchLots(urlParams);
			setSolutionBatchLots(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const handleBatchChange = (batchId: string) => {
		const selected = solutionBatches.find((b) => b.id === batchId);
		if (selected) {
			setSelectedSolutionBatch(selected);
			setMixingParameters((prev: SolutionPowderMixingPayload) => ({
				...prev,
				solution_batch_id: selected.id,
			}));
			setErrors(prev => ({
				...prev,
				solutionBatch: "",
			}));
		}
	};

	const handleLotChange = (lotId: string) => {
		const selected = solutionBatchLots.find((b) => b.id === lotId);
		if (selected) {
			setSelectedSolutionBatchLot(selected);
			setMixingParameters((prev: SolutionPowderMixingPayload) => ({
				...prev,
				solution_lot_id: selected.id,
			}));
			setErrors(prev => ({
				...prev,
				solutionLot: "",
			}));
		}
	};

	useEffect(() => {
		if (isReadOnly) return;
		getSolutionBatches();
	}, [isReadOnly]);

	useEffect(() => {
		if (isReadOnly) return;
		setSelectedSolutionBatchLot(null);
		getSolutionBatchLots();
	}, [selectedSolutionBatch, isReadOnly]);

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
			<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
				<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
					02
				</div>
				<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
					{t("mediaBatchInit.preparation.solutionSolventSelection")}
				</h3>
			</div>
			<div className="p-6 grid grid-cols-12 gap-8 items-center">
				<div className="col-span-12 lg:col-span-7 space-y-6">
					<div className="space-y-1.5">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.selectBatch")}  <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<select
								value={isReadOnly ? "view-batch" : (selectedSolutionBatch?.id ?? "")}
								disabled={isReadOnly}
								className={`w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-700 outline-none appearance-none ${
									isReadOnly ? "bg-slate-100 opacity-70 cursor-not-allowed" : "bg-slate-50/50"
								}`}
								onChange={(e) => handleBatchChange(e.target.value)}
							>
								{isReadOnly ? (
									<option value="view-batch">{solutionConsumption.solution_batch_number}</option>
								) : (
									<>
										<option value="" disabled>
											{t("mediaBatchInit.preparation.selectBatch")}
										</option>
										{solutionBatches.map((batch) => (
											<option key={batch.id} value={batch.id}>
												{batch.batch_name}
											</option>
										))}
									</>
								)}
							</select>
							<span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">
								expand_more
							</span>
						</div>	
						{errors.solutionBatch && <p className="text-[11px] text-red-500">{errors.solutionBatch}</p>}
					</div>
					<div className="space-y-1.5">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.selectLot")}  <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<select
								value={isReadOnly ? "view-lot" : (selectedSolutionBatchLot?.id ?? "")}
								disabled={isReadOnly}
								className={`w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-700 outline-none appearance-none ${
									isReadOnly ? "bg-slate-100 opacity-70 cursor-not-allowed" : "bg-slate-50/50"
								}`}
								onChange={(e) => handleLotChange(e.target.value)}
							>
								{isReadOnly ? (
									<option value="view-lot">{solutionConsumption.solution_lot_code}</option>
								) : (
									<>
										<option value="" disabled>
											{t("mediaBatchInit.preparation.selectLot")}
										</option>
										{solutionBatchLots.map((lot) => (
											<option key={lot.id} value={lot.id}>
												{lot.lot_code}
											</option>
										))}
									</>
								)}
							</select>
							<span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">
								expand_more
							</span>
						</div>
						{!isReadOnly && (
							<div className="flex items-center gap-1.5 text-[8px] font-black text-emerald-500 uppercase">
								<span className="material-symbols-outlined text-[10px]">
									water_drop
								</span>
								{selectedSolutionBatchLot?.available_stock}
							</div>
						)}
						{errors.solutionLot && <p className="text-[11px] text-red-500">{errors.solutionLot}</p>}
					</div>
				</div>
				<div className="col-span-12 lg:col-span-5">
					<div className="bg-[#FBFDFB] border border-emerald-100 rounded-2xl p-6 relative group overflow-hidden">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">
							{t("mediaBatchInit.preparation.quantityTaken")} (ML)  <span className="text-red-500">*</span>
						</label>
						<div className="flex items-center justify-between">
							<span className="text-4xl font-black text-slate-800">
								{isReadOnly
									? solutionConsumption.quantity_used
									: (mixingParameters.solution_volume_used ?? 0.0)}
							</span>
							<div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20">
								<span className="material-symbols-outlined text-sm font-black">
									check
								</span>
							</div>
						</div>
						<p className="text-[10px] text-slate-300 italic mt-4 font-medium uppercase tracking-tight">
							{t("mediaBatchInit.preparation.dispensedViaCalibrated")}
						</p>
						<div className="absolute -bottom-4 -right-4 text-emerald-500/5 group-hover:scale-110 transition-transform">
							<span className="material-symbols-outlined text-8xl">
								precision_manufacturing
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MediaSolutionSelectionBox;
